import { Switch, Typography, Flex, Form } from 'antd';

import { ExtendedMessage } from '@app/components';
import { PREFIX as GLOBAL_PREFIX } from '@app/constants/appPrefix';
import type { NRecordReminders } from '@app/types';

import { LabelWithTime } from '@pages/Notifications/components/LabelWithTime';
import { ReminderFiles } from '@pages/Notifications/components/ReminderFiles';

import type { FormInstance } from 'antd';
import type { FC, RefObject } from 'react';

const { Title, Paragraph, Text } = Typography;
const { useWatch } = Form;

const COMPONENT_CLASS_NAME = `${GLOBAL_PREFIX}notification-create-message`;

type TNotificationCreateMessageProps = {
  form: FormInstance;
  createRecordBoxRef: RefObject<HTMLDivElement>;
  reminder: NRecordReminders.ModelRecordReminder | null;
  disabled?: boolean;
};

export const NotificationCreateMessage: FC<TNotificationCreateMessageProps> = ({
  form,
  createRecordBoxRef,
  reminder,
  disabled = false,
}) => {
  const hoursValue = form.getFieldValue('createRecordIntervalHours') || 0;
  const minutesValue = form.getFieldValue('createRecordIntervalMinutes') || 0;

  const handleHoursChange = (value: number) => {
    form.setFieldsValue({ createRecordIntervalHours: value });
  };

  const handleMinutesChange = (value: number) => {
    form.setFieldsValue({ createRecordIntervalMinutes: value });
  };

  const isCreateRecordStatus = useWatch('createRecordStatus', form);

  return (
    <div className={COMPONENT_CLASS_NAME} ref={createRecordBoxRef}>
      <Flex justify="space-between" align="center">
        <Title
          level={4}
          className={`${COMPONENT_CLASS_NAME}__header`}
          data-testid="notification-block-title"
        >
          Уведомление клиенту о создании записи
        </Title>
        <Form.Item
          name="createRecordStatus"
          className={`${COMPONENT_CLASS_NAME}__switch`}
        >
          <Switch
            data-testid={
              form.getFieldValue('createRecordStatus')
                ? 'active-notification'
                : 'inactive-notification'
            }
            disabled={disabled}
          />
        </Form.Item>
      </Flex>

      {isCreateRecordStatus && (
        <>
          <Paragraph
            className={`${COMPONENT_CLASS_NAME}__description`}
            data-testid="notification-description"
          >
            Настройте сообщение, отправляемое клиенту в WhatsApp, после создания
            записи.
          </Paragraph>

          <LabelWithTime
            componentClassName={COMPONENT_CLASS_NAME}
            type="after"
            minutesFormItemName="createRecordIntervalMinutes"
            hoursFormItemName="createRecordIntervalHours"
            onHoursChange={handleHoursChange}
            onMinutesChange={handleMinutesChange}
            hoursValue={hoursValue}
            minutesValue={minutesValue}
            disabled={disabled}
          />

          <Text
            className={`${COMPONENT_CLASS_NAME}__label`}
            data-testid="notification-title"
          >
            Текст уведомления
          </Text>
          <ExtendedMessage
            form={form}
            socialType="whatsapp"
            formItemName="createRecordMessage"
            formItemClassName={`${COMPONENT_CLASS_NAME}__textarea`}
            textAreaDataTestId="notification-template-text"
            disabled={disabled}
          />

          <ReminderFiles
            reminder={reminder}
            componentClassName={COMPONENT_CLASS_NAME}
            disabled={disabled}
          />
        </>
      )}
    </div>
  );
};
